import type { CSSProperties } from 'react';
import type { WorkflowProgressStatus, WorkflowProgressStep } from './types';
interface ProgressRailProps {
  steps: WorkflowProgressStep[];
  /** When true, all steps render as completed (form / success phases). */
  allComplete?: boolean;
  ariaLabel?: string;
}

type ProgressIconState = 'pending' | 'active' | 'done';

function resolveIconState(
  status: WorkflowProgressStatus,
  forceComplete?: boolean
): ProgressIconState {
  if (forceComplete || status === 'completed') return 'done';
  if (status === 'in-progress') return 'active';
  return 'pending';
}

function PendingIcon() {
  return (
    <svg className="flow-rail__icon-svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5" fill="#ffffff" />
    </svg>
  );
}

function ActiveSpinnerIcon() {
  return (
    <svg
      className="flow-rail__icon-svg flow-rail__spinner-svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden
    >
      {Array.from({ length: 8 }, (_, i) => (
        <line
          key={i}
          x1="9"
          y1="2.75"
          x2="9"
          y2="5.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          transform={`rotate(${i * 45} 9 9)`}
          opacity={0.3 + (i / 7) * 0.7}
        />
      ))}
    </svg>
  );
}

function DoneIcon() {
  return (
    <svg className="flow-rail__icon-svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="9" className="flow-rail__done-circle" />
      <path
        d="M5.5 9.5L7.5 11.5L12.5 6.5"
        stroke="var(--color-text-inverse)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProgressIcon({
  status,
  forceComplete,
}: {
  status: WorkflowProgressStatus;
  forceComplete?: boolean;
}) {
  const state = resolveIconState(status, forceComplete);

  return (
    <span className={`flow-rail__icon flow-rail__icon--${state}`} aria-hidden>
      {state === 'done' ? <DoneIcon /> : state === 'active' ? <ActiveSpinnerIcon /> : <PendingIcon />}
    </span>
  );
}

export default function ProgressRail({
  steps,
  allComplete = false,
  ariaLabel = 'Progress',
}: ProgressRailProps) {
  return (
    <nav className="flow-rail" aria-label={ariaLabel}>
      <ul className="flow-rail__list">
        {steps.map((step, index) => {
          const isActive = !allComplete && step.status === 'in-progress';
          const isDone = allComplete || step.status === 'completed';

          return (
            <li
              key={step.id}
              className={`flow-rail__row flow-rail__row--visible${isActive ? ' flow-rail__row--active' : ''}${isDone ? ' flow-rail__row--done' : ''}`}
              style={{ '--row-index': index, animationDelay: `${index * 0.15}s` } as CSSProperties}
            >
              <ProgressIcon status={step.status} forceComplete={allComplete} />
              <span className="flow-rail__text">{step.label}</span>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
